import { useEffect, useEffectEvent, useId, useRef } from "react";

import { type DialogProps as MuiDialogProps } from "@mui/material";

import { type DialogStatusIntent } from "ui/dialog/dialog.constants";
import { dialogHistoryEntry, isDialogHistoryEntry } from "ui/dialog/dialog.helpers";
import { type DialogProps } from "ui/dialog/dialog.types";

type UseDialogOptions = Pick<DialogProps, "open" | "onClose"> & {
  closable: boolean;
  closeOnBrowserBack: boolean;
  intent: NonNullable<DialogProps["intent"]>;
};

export type DialogState = {
  /** `aria-labelledby` target: the headline's id. */
  headlineId: string;
  /** `aria-describedby` target: the description's id. */
  descriptionId: string;
  /** `intent` without `inherit`, which draws no rail colour and no glyph. */
  statusIntent: DialogStatusIntent | null;
  /** For MUI's `onClose`: filters the reason before calling the caller's `onClose()`. */
  handleClose: NonNullable<MuiDialogProps["onClose"]>;
};

export const useDialog = ({
  open,
  onClose,
  closable,
  closeOnBrowserBack,
  intent,
}: UseDialogOptions): DialogState => {
  const headlineId = useId();
  const descriptionId = useId();

  const isBackEnabled = closeOnBrowserBack && open && closable;

  /* Call sites pass inline arrows, so `onClose` is a new function every render. Keeping it out of
     the effect's deps is what stops each re-render from pushing another history entry. */
  const handlePopState = useEffectEvent(() => onClose());
  const pendingBackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isBackEnabled) {
      return;
    }

    /* A cleanup that ran a moment ago left its entry in place and only scheduled the `back()` — StrictMode
       replays every mount that way in dev. Take the entry over instead of stacking a second one. */
    if (pendingBackRef.current === null) {
      window.history.pushState(dialogHistoryEntry(headlineId), "", window.location.href);
    } else {
      clearTimeout(pendingBackRef.current);
      pendingBackRef.current = null;
    }

    /* Landing on this dialog's own entry means Back closed a dialog stacked above it, not this one.
       Otherwise Back has taken the entry, so it goes straight back before `onClose` runs: a caller may keep
       the dialog open — a dirty form asks "Leave without saving?" — and without it the next Back would
       leave the page under the open dialog. When the dialog does close, the cleanup takes it off again. */
    const listener = (event: PopStateEvent) => {
      if (isDialogHistoryEntry(event.state, headlineId)) {
        return;
      }

      window.history.pushState(dialogHistoryEntry(headlineId), "", window.location.href);
      handlePopState();
    };

    window.addEventListener("popstate", listener);

    return () => {
      window.removeEventListener("popstate", listener);

      /* Closed by anything but Back — the close button, Escape, a submit — so the entry is still on top, and
         left there it would swallow the next Back press. Not when the caller navigated away: the router's
         entry is on top then, and `back()` would undo the navigation. Re-checked when the timer fires, in
         case the next dialog has pushed its own entry by then. */
      if (isDialogHistoryEntry(window.history.state, headlineId)) {
        pendingBackRef.current = setTimeout(() => {
          pendingBackRef.current = null;

          if (isDialogHistoryEntry(window.history.state, headlineId)) {
            window.history.back();
          }
        });
      }
    };
  }, [isBackEnabled, headlineId]);

  const handleClose: DialogState["handleClose"] = (_event, reason) => {
    if (reason === "backdropClick") return;
    /* v9 removed `disableEscapeKeyDown`: Escape always reaches `onClose` now, so a
       non-closeable dialog has to reject the reason itself. */
    if (reason === "escapeKeyDown" && !closable) return;
    onClose();
  };

  const statusIntent = intent === "inherit" ? null : intent;

  return { headlineId, descriptionId, statusIntent, handleClose };
};
